import React, { ReactElement, useMemo } from "react";

import Link, { LinkProps } from "next/link";

import { logError } from "utils/logger";

export type SmartLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
    LinkProps & {
        children: JSX.Element;
    } & React.RefAttributes<HTMLAnchorElement>;

// Problem:
// Prefetch is enabled even when set to false, all setting false does is set prefetching to be performed
// on hover and because we have external & internal links in the site, any external links will be prefetched
// causing 404's on .json files generated by Next on build time.
//
// Solution:
// This is a wrapper around next/link that completely prevents preloading of links when hovering over them
// if the prefetch prop is set to false. This is useful for links that are not in the viewport. It also offers
// some protection over the children prop to ensure that only one child is passed in and that it is an
// <a> element.

const SmartLink: React.FC<SmartLinkProps> = ({ prefetch, children, ...linkProps }) => {
    const count = React.Children.count(children);

    const clonedChildHref = useMemo(() => {
        if (count === 1) {
            const fakeUrl = "https://fake.url";
            return new URL(linkProps.href as string, fakeUrl).href.replace(fakeUrl, "");
        }
    }, [count, linkProps.href]);

    if (count > 1 || count === 0) {
        const errorMessage = "SmartLink requires exactly <a> one child";
        logError(errorMessage, children);
        return null;
    }

    const clonedChild: ReactElement = React.cloneElement(children as unknown as any, {
        ...linkProps,
        href: clonedChildHref,
    });

    React.Children.forEach(clonedChild as unknown as any, (child: ReactElement) => {
        if (child.type !== "a") {
            const errorMessage = "SmartLink only supports one child <a> element";
            logError(errorMessage, clonedChild);
        }
    });

    return !prefetch ? <>{clonedChild}</> : <Link {...clonedChild.props} />;
};

export default SmartLink;
